import React from 'react';
import { cn } from '../../utils';
import {
  FullSimulationResult,
  WorkflowSimulationResult,
  ExecutionSimulationResult,
  SimulatedWorkflowStep,
  SimulatedMatchedUser,
  MatchedIdentifierDto,
  WorkflowRecommendationResult,
  DiscoveredField
} from '../../types';
import { RecommendationsPanel } from './recommendations-panel';

interface SimulationResultsPanelProps {
  result: FullSimulationResult | null;
  isLoading?: boolean;
  className?: string;
  /** Called when user wants recommendations after no match */
  onGetRecommendations?: () => void;
  /** Loading state for recommendations */
  isLoadingRecommendations?: boolean;
  /** Recommendations result to show when no match */
  recommendations?: WorkflowRecommendationResult | null;
  /** Called when user wants to add an identifier from recommendations to a draft workflow */
  onAddIdentifier?: (identifier: {
    name: string;
    jsonPath: string;
    operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'regex' | 'greaterThan' | 'lessThan';
    value?: string;
    regexPattern?: string;
  }) => void;
  /** Called when user wants to create an order attribute from a discovered field */
  onCreateOrderAttribute?: (field: DiscoveredField) => void;
}

export function SimulationResultsPanel({
  result,
  isLoading = false,
  className,
  onGetRecommendations,
  isLoadingRecommendations = false,
  recommendations,
  onAddIdentifier,
  onCreateOrderAttribute
}: SimulationResultsPanelProps) {
  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center h-64', className)}>
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-secondary-600 dark:text-secondary-400">Running simulation...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className={cn('flex items-center justify-center h-64 text-center', className)}>
        <div>
          <svg className="w-16 h-16 text-secondary-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
            No Simulation Results
          </h3>
          <p className="text-secondary-600 dark:text-secondary-400 max-w-sm">
            Enter JSON data on the left and click "Run Simulation" to see which workflow would match and how it would execute.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6 overflow-auto', className)}>
      {/* Summary Banner */}
      <SummaryBanner 
        result={result} 
        onGetRecommendations={onGetRecommendations}
        isLoadingRecommendations={isLoadingRecommendations}
      />

      {/* Match Results */}
      {result.matchResult && (
        <MatchResultsSection matchResult={result.matchResult} />
      )}

      {/* Execution Results */}
      {result.executionResult && (
        <ExecutionResultsSection executionResult={result.executionResult} />
      )}

      {/* Recommendations when no match found */}
      {!result.matchResult?.hasMatch && recommendations && (
        <div className="border-t border-secondary-200 dark:border-secondary-700 pt-6">
          <RecommendationsPanel 
            result={recommendations} 
            onAddIdentifier={onAddIdentifier}
            onCreateOrderAttribute={onCreateOrderAttribute}
          />
        </div>
      )}
    </div>
  );
}

function SummaryBanner({ 
  result, 
  onGetRecommendations,
  isLoadingRecommendations = false
}: { 
  result: FullSimulationResult;
  onGetRecommendations?: () => void;
  isLoadingRecommendations?: boolean;
}) {
  const hasMatch = result.matchResult?.hasMatch;
  
  return (
    <div className={cn(
      'rounded-lg p-4 border',
      hasMatch
        ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
        : 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800'
    )}>
      <div className="flex items-start gap-3">
        {hasMatch ? (
          <svg className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ) : (
          <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )}
        <div className="flex-1">
          <h3 className={cn(
            'font-semibold',
            hasMatch ? 'text-green-800' : 'text-yellow-800'
          )}>
            {hasMatch ? 'Workflow Matched!' : 'No Match Found'}
          </h3>
          <p className={cn(
            'text-sm mt-1',
            hasMatch ? 'text-green-700 dark:text-green-400' : 'text-yellow-700 dark:text-yellow-400'
          )}>
            {result.summary}
          </p>
          <p className="text-xs mt-2 text-secondary-500 dark:text-secondary-400">
            Simulated at {new Date(result.simulatedAt).toLocaleString()}
          </p>
          
          {/* Show recommendation prompt when no match */}
          {!hasMatch && onGetRecommendations && (
            <div className="mt-4 pt-4 border-t border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 mb-3">
                <i className="fa-solid fa-lightbulb mr-2 text-yellow-500" />
                Would you like suggestions for setting up a workflow that would match this order?
              </p>
              <button
                onClick={onGetRecommendations}
                disabled={isLoadingRecommendations}
                className={cn(
                  'inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  isLoadingRecommendations
                    ? 'bg-yellow-200 text-yellow-600 cursor-wait'
                    : 'bg-yellow-500 text-white hover:bg-yellow-600'
                )}
              >
                {isLoadingRecommendations ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-wand-magic-sparkles mr-2" />
                    Get Workflow Recommendations
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchResultsSection({ matchResult }: { matchResult: WorkflowSimulationResult }) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        Match Analysis
      </h3>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label="Workflows Evaluated"
          value={matchResult.totalEvaluated}
          color="secondary"
        />
        <StatCard
          label="Workflows Matched"
          value={matchResult.totalMatched}
          color={matchResult.totalMatched > 0 ? 'green' : 'yellow'}
        />
        <StatCard
          label="Match Score"
          value={matchResult.bestMatch?.matchScore ? `${matchResult.bestMatch.matchScore}%` : 'N/A'}
          color={matchResult.bestMatch?.matchScore && matchResult.bestMatch.matchScore >= 80 ? 'green' : 'yellow'}
        />
      </div>

      {/* Best Match Details */}
      {matchResult.bestMatch && (
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4">
          <h4 className="font-medium text-secondary-900 dark:text-white mb-3">
            Best Match: {matchResult.bestMatch.workflowName}
          </h4>
        </div>
      )}

      {/* Alternative Matches */}
      {matchResult.alternativeMatches && matchResult.alternativeMatches.length > 0 && (
        <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-4">
          <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
            Alternative Matches ({matchResult.alternativeMatches.length})
          </h4>
          <ul className="space-y-1">
            {matchResult.alternativeMatches.map((alt, index) => (
              <li key={index} className="text-sm text-secondary-600 dark:text-secondary-400 flex justify-between">
                <span>{alt.workflowName}</span>
                <span className="text-secondary-500 dark:text-secondary-400">{alt.matchScore}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ExecutionResultsSection({ executionResult }: { executionResult: ExecutionSimulationResult }) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        Execution Preview
      </h3>

      {/* Workflow Info */}
      {executionResult.workflow && (
        <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-primary-900">
                {executionResult.workflow.name}
              </h4>
              <p className="text-sm text-primary-700 mt-1">
                {executionResult.workflow.description || 'No description'}
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-primary-700">
                {executionResult.workflow.totalLevels} step(s)
              </div>
              <div className="text-xs text-primary-600 mt-1">
                Est. {executionResult.estimatedDuration}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Steps */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-200">Workflow:</h4>
        {executionResult.workflowSteps?.map((level, index) => (
          <ApprovalLevelCard key={index} level={level} />
        )) || (
          <div className="text-sm text-gray-500 dark:text-secondary-400 italic">No workflow steps to display</div>
        )}
      </div>
    </div>
  );
}

function ApprovalLevelCard({ level }: { level: SimulatedWorkflowStep }) {
  const hasWarnings = level.warnings && level.warnings.length > 0;
  const hasUsers = level.matchedUsers && level.matchedUsers.length > 0;

  return (
    <div className={cn(
      'rounded-lg border p-4',
      level.wouldBeSkipped
        ? 'bg-secondary-50 dark:bg-secondary-900 border-secondary-200 dark:border-secondary-700 opacity-60'
        : hasWarnings
          ? 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800'
          : 'bg-white dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700'
    )}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={cn(
            'inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium',
            level.wouldBeSkipped
              ? 'bg-secondary-200 dark:bg-secondary-600 text-secondary-600 dark:text-secondary-400'
              : 'bg-primary-100 text-primary-700'
          )}>
            {level.step}
          </span>
          <div>
            <h5 className="font-medium text-secondary-900 dark:text-white">{level.name}</h5>
            {level.description && (
              <p className="text-xs text-secondary-500 dark:text-secondary-400">{level.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {level.wouldBeSkipped && (
            <span className="text-xs bg-secondary-200 dark:bg-secondary-600 text-secondary-700 dark:text-secondary-200 px-2 py-1 rounded">
              Skipped
            </span>
          )}
          {level.timeoutHours && (
            <span className="text-xs text-secondary-500 dark:text-secondary-400">
              {level.timeoutHours}h timeout
            </span>
          )}
        </div>
      </div>

      {/* Skip Reason */}
      {level.wouldBeSkipped && level.skipReason && (
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3 italic">
          {level.skipReason}
        </p>
      )}

      {/* Users */}
      {!level.wouldBeSkipped && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-secondary-500 dark:text-secondary-400">
            <span>
              Parallel execution: {level.parallelApproval ? 'Yes' : 'No'}
              {level.requiredApprovals > 1 && ` (${level.requiredApprovals} required)`}
            </span>
            <span>{level.matchedUsers?.length || 0} user(s)</span>
          </div>
          
          {hasUsers ? (
            <div className="flex flex-wrap gap-2">
              {level.matchedUsers.map((user, index) => (
                <UserBadge key={index} user={user} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-yellow-700 dark:text-yellow-400">
              ⚠️ No users configured for this level
            </p>
          )}
        </div>
      )}

      {/* Warnings */}
      {hasWarnings && (
        <div className="mt-3 pt-3 border-t border-yellow-200 dark:border-yellow-800">
          {level.warnings.map((warning, index) => (
            <p key={index} className="text-xs text-yellow-700 dark:text-yellow-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {warning}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function UserBadge({ user }: { user: SimulatedMatchedUser }) {
  return (
    <div className={cn(
      'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
      user.isActive
        ? 'bg-green-100 dark:bg-green-900 text-green-800'
        : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400'
    )}>
      <div className="w-6 h-6 rounded-full bg-white dark:bg-secondary-800 flex items-center justify-center text-xs font-medium">
        {user.userName.split(' ').map(n => n[0]).join('').toUpperCase()}
      </div>
      <div>
        <span className="font-medium">{user.userName}</span>
        <span className="text-xs ml-1 opacity-75">({user.role})</span>
      </div>
      {!user.isActive && (
        <span className="text-xs bg-secondary-200 dark:bg-secondary-600 px-1 rounded">inactive</span>
      )}
    </div>
  );
}

function IdentifierMatchRow({ identifier }: { identifier: MatchedIdentifierDto }) {
  return (
    <div className={cn(
      'flex items-center justify-between py-1.5 px-2 rounded text-sm',
      identifier.matched
        ? 'bg-green-50 dark:bg-green-950'
        : 'bg-red-50 dark:bg-red-950'
    )}>
      <div className="flex items-center gap-2">
        {identifier.matched ? (
          <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
        <span className={identifier.matched ? 'text-green-800' : 'text-red-800'}>
          {identifier.matchIdentifierName}
        </span>
      </div>
      <div className="text-xs text-secondary-500 dark:text-secondary-400">
        {identifier.extractedValue !== undefined && identifier.extractedValue !== null ? (
          <span className="font-mono bg-secondary-100 dark:bg-secondary-700 px-1 rounded">
            {String(identifier.extractedValue).substring(0, 30)}
            {String(identifier.extractedValue).length > 30 && '...'}
          </span>
        ) : (
          <span className="italic">{identifier.reason || 'No value'}</span>
        )}
      </div>
    </div>
  );
}

function StatCard({ 
  label, 
  value, 
  color 
}: { 
  label: string; 
  value: number | string; 
  color: 'secondary' | 'green' | 'yellow' | 'red';
}) {
  const colors = {
    secondary: 'bg-secondary-50 dark:bg-secondary-900 border-secondary-200 dark:border-secondary-700 text-secondary-900 dark:text-white',
    green: 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-900',
    yellow: 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800 text-yellow-900',
    red: 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-900',
  };

  return (
    <div className={cn('rounded-lg border p-3 text-center', colors[color])}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs opacity-75">{label}</div>
    </div>
  );
}
